import React, { useState, useEffect } from 'react';
import '../styles/SetupChecklist.css';
import apiService from '../services/api';

const SetupChecklist = ({ theme, onUploadDocument, toggleSourceSelector, onNewChat, onClose }) => {
  const [checklistStatus, setChecklistStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load checklist status on component mount
  useEffect(() => {
    const fetchChecklistStatus = async () => {
      try {
        setIsLoading(true);
        const status = await apiService.getSetupChecklistStatus();
        setChecklistStatus(status);
        setIsLoading(false);
      } catch (error) {
        console.error('Error loading checklist status:', error);
        setIsLoading(false);
      }
    };

    fetchChecklistStatus();
  }, []);

  // Handle manually marking an item as complete
  const handleMarkComplete = async (itemId) => {
    try {
      const updatedStatus = await apiService.completeChecklistItem(itemId);
      setChecklistStatus(updatedStatus);
    } catch (error) {
      console.error('Error marking item as complete:', error);
    }
  };

  // Handle dismissing the checklist
  const handleDismiss = async () => {
    try {
      // Instead of just dismissing the checklist, we'll reset it to its initial state
      // so it can show up fresh next time
      await apiService.resetEntireChecklist();
      
      // Close the checklist modal
      onClose();
    } catch (error) {
      console.error('Error resetting checklist:', error);
    }
  };

  // Check if all items are complete to determine progress
  const getCompletionPercentage = () => {
    if (!checklistStatus || !checklistStatus.items || checklistStatus.items.length === 0) return 0;
    
    const completedCount = checklistStatus.items.filter(item => item.completed).length;
    return Math.round((completedCount / checklistStatus.items.length) * 100);
  };

  // Get number of incomplete items for badge
  const getIncompleteCount = () => {
    if (!checklistStatus || !checklistStatus.items) return 0;
    return checklistStatus.items.filter(item => !item.completed).length;
  };

  // Handle item-specific actions
  const handleItemAction = (itemId) => {
    switch (itemId) {
      case 'model_selection':
        // Open model selector
        // This would typically open your model selection interface
        onClose(); // Close checklist after action
        break;
      case 'test_question':
        // Start a new chat to ask a test question
        onNewChat();
        onClose(); // Close checklist after action
        break;
      case 'configure_sources':
        // Open source selector
        toggleSourceSelector();
        onClose(); // Close checklist after action
        break;
      case 'system_settings':
        // Open settings panel
        // This would typically open your settings interface
        onClose(); // Close checklist after action
        break;
      default:
        console.log(`No specific action for ${itemId}`);
    }
  };

  // If checklist is dismissed or still loading, don't render it
  if (isLoading || (checklistStatus && checklistStatus.dismissed)) {
    return null;
  }

  const completionPercentage = getCompletionPercentage();
  const completedItems = checklistStatus?.items ? checklistStatus.items.filter(item => item.completed).length : 0;
  const totalItems = checklistStatus?.items ? checklistStatus.items.length : 0;

  // Return the checklist as a modal dialog with floating progress bar
  return (
    <div className="modal-overlay">
      {/* Floating progress bar above modal */}
      <div className="checklist-floating-progress">
        <div className="progress-text">
          {completedItems} of {totalItems} tasks complete
        </div>
        <div className="progress-bar-container">
          <div 
            className="progress-bar-fill" 
            style={{ width: `${completionPercentage}%` }}
          />
        </div>
      </div>

      <div className={`setup-checklist-modal ${theme}`}>
        <div className="setup-checklist-header">
          <h3>Setup Checklist</h3>
          <div className="actions">
            <button onClick={onClose} title="Close">
              <span className="material-icons">close</span>
            </button>
          </div>
        </div>
        <div className="setup-checklist-content">
          <div className="setup-checklist-items">
            {checklistStatus && checklistStatus.items && checklistStatus.items.map(item => (
              <div 
                key={item.id} 
                className={`checklist-item ${item.completed ? 'completed' : ''}`}
              >
                <div className="checklist-item-checkbox">
                  <input 
                    type="checkbox" 
                    checked={item.completed} 
                    onChange={() => handleMarkComplete(item.id)}
                    id={`checkbox-${item.id}`}
                  />
                </div>
                <div className="checklist-item-content">
                  <div className="checklist-item-title">
                    <label htmlFor={`checkbox-${item.id}`}>{item.title}</label>
                    {item.completed && (
                      <span className="checklist-item-complete">Complete</span>
                    )}
                  </div>
                  <div className="checklist-item-description">
                    {item.description}
                  </div>
                  {!item.completed && (
                    <div className="checklist-item-actions">
                      <button 
                        className="start-btn"
                        onClick={() => handleItemAction(item.id)}
                      >
                        <span className="material-icons">arrow_forward</span>
                        Start
                      </button>
                      <button 
                        className="complete-btn"
                        onClick={() => handleMarkComplete(item.id)}
                      >
                        <span className="material-icons">check</span>
                        Mark Complete
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          {completionPercentage === 100 && (
            <div className="checklist-complete-message">
              <span className="material-icons success-icon">check_circle</span>
              <p>All setup tasks are complete!</p>
              <button onClick={handleDismiss} className="dismiss-btn">
                Dismiss Checklist
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SetupChecklist;